export const intentActionVariants = {
  primary: "bg-comp-accent text-comp-accent-foreground hover:brightness-[1.1]",
  destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  success: "bg-success text-success-foreground hover:bg-success/90",
  warning: "bg-warning text-warning-foreground hover:bg-warning/90",
  info: "bg-info text-info-foreground hover:bg-info/90",
  neutral: "border border-comp-border-subtle bg-comp-subtle text-comp-text-secondary hover:border-comp-border hover:bg-comp-elevated hover:text-comp-text-primary",
} as const;

export const intentSurfaceVariants = {
  outline: "border border-comp-border-subtle bg-comp-surface text-comp-text-primary hover:border-comp-border hover:bg-comp-subtle",
  secondary: "border border-comp-border-subtle bg-comp-subtle text-comp-text-secondary hover:border-comp-border hover:bg-comp-elevated hover:text-comp-text-primary",
  ghost: "text-comp-text-tertiary hover:bg-comp-subtle hover:text-comp-text-primary",
} as const;

export const intentStatusVariants = {
  destructive: "border border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground",
  success: "border border-success-border bg-success-subtle text-success-subtle-foreground",
  info: "border border-info-border bg-info-subtle text-info-subtle-foreground",
  warning: "border border-warning-border bg-warning-subtle text-warning-subtle-foreground",
  neutral: "border border-neutral-border bg-neutral-subtle text-neutral-subtle-foreground",
} as const;

export const intentTextVariants = {
  destructive: "text-destructive",
  success: "text-success",
  info: "text-info",
  warning: "text-warning",
  neutral: "text-neutral-subtle-foreground",
} as const;

export const intentSubtleTextVariants = {
  destructive: "text-destructive-subtle-foreground",
  success: "text-success-subtle-foreground",
  info: "text-info-subtle-foreground",
  warning: "text-warning-subtle-foreground",
  neutral: "text-neutral-subtle-foreground",
} as const;

export const intentIndicatorVariants = {
  destructive: "bg-destructive",
  success: "bg-success",
  info: "bg-info",
  warning: "bg-warning",
  neutral: "bg-neutral",
} as const;

const filledActionDepthStyles = "[box-shadow:var(--control-button-shadow-solid)] hover:[box-shadow:var(--control-button-shadow-solid-hover)] active:[box-shadow:var(--control-button-shadow-solid-active)]";

export const buttonVariantStyles = {
  solid: "motion-pressable active:scale-[var(--motion-press-scale)] focus-visible:active:scale-100",
  soft: "motion-pressable active:scale-[var(--motion-press-scale)] focus-visible:active:scale-100",
  outline: "motion-pressable [box-shadow:none] active:scale-[var(--motion-press-scale)] focus-visible:active:scale-100",
  ghost: "motion-pressable [box-shadow:none] active:scale-[var(--motion-press-scale)] focus-visible:active:scale-100",
  link: "border-transparent bg-transparent underline-offset-4 [box-shadow:none] hover:underline active:scale-100",
} as const;

export const buttonColorStyles = {
  solid: {
    primary: `${filledActionDepthStyles} border-[var(--control-button-border-solid)] bg-comp-accent text-comp-accent-foreground hover:brightness-[1.1] active:brightness-[0.98]`,
    secondary: "[box-shadow:none] border-[var(--control-surface-border)] bg-[var(--control-surface-bg)] text-[var(--control-surface-text)] hover:border-comp-border hover:bg-[var(--control-surface-bg-hover)] active:brightness-[0.97]",
    danger: `${filledActionDepthStyles} border-[var(--control-button-border-solid)] bg-destructive text-destructive-foreground hover:brightness-[1.08] active:brightness-[0.96]`,
    success: `${filledActionDepthStyles} border-[var(--control-button-border-solid)] bg-success text-success-foreground hover:brightness-[1.08] active:brightness-[0.96]`,
    warning: `${filledActionDepthStyles} border-[var(--control-button-border-solid)] bg-warning text-warning-foreground hover:brightness-[1.06] active:brightness-[0.96]`,
    info: `${filledActionDepthStyles} border-[var(--control-button-border-solid)] bg-info text-info-foreground hover:brightness-[1.08] active:brightness-[0.96]`,
    neutral: "[box-shadow:none] border-comp-border-subtle bg-comp-subtle text-comp-text-secondary hover:border-comp-border hover:bg-comp-elevated active:brightness-[0.97]",
  },
  soft: {
    primary: "border-[var(--comp-accent-tint)] bg-[var(--comp-accent-tint)] text-[var(--comp-accent)] hover:brightness-[0.95] active:brightness-[0.9]",
    secondary: "border-[var(--comp-bg-subtle)] bg-comp-subtle text-comp-text-secondary hover:bg-comp-elevated hover:text-comp-text-primary",
    danger: "border-destructive-subtle bg-destructive-subtle text-destructive-subtle-foreground hover:bg-destructive-subtle/80",
    success: "border-success-subtle bg-success-subtle text-success-subtle-foreground hover:bg-success-subtle/80",
    warning: "border-warning-subtle bg-warning-subtle text-warning-subtle-foreground hover:bg-warning-subtle/80",
    info: "border-info-subtle bg-info-subtle text-info-subtle-foreground hover:bg-info-subtle/80",
    neutral: "border-neutral-subtle bg-neutral-subtle text-neutral-subtle-foreground hover:bg-neutral-subtle/80",
  },
  outline: {
    primary: "border-[var(--comp-accent)] bg-transparent text-[var(--comp-accent)] hover:bg-[var(--comp-accent-tint)] active:brightness-[0.95]",
    secondary: "border-comp-border-subtle bg-comp-surface text-comp-text-primary hover:border-comp-border hover:bg-comp-subtle",
    danger: "border-destructive-border bg-transparent text-destructive hover:bg-destructive-subtle active:bg-destructive-subtle/90",
    success: "border-success-border bg-transparent text-success hover:bg-success-subtle active:bg-success-subtle/90",
    warning: "border-warning-border bg-transparent text-warning hover:bg-warning-subtle active:bg-warning-subtle/90",
    info: "border-info-border bg-transparent text-info hover:bg-info-subtle active:bg-info-subtle/90",
    neutral: "border-neutral-border bg-transparent text-neutral-subtle-foreground hover:bg-neutral-subtle active:bg-neutral-subtle/90",
  },
  ghost: {
    primary: "border-transparent bg-transparent text-[var(--comp-accent)] hover:bg-[var(--comp-accent-tint)] active:brightness-[0.95]",
    secondary: "border-transparent bg-transparent text-comp-text-tertiary hover:bg-comp-subtle hover:text-comp-text-primary active:bg-comp-elevated",
    danger: "border-transparent bg-transparent text-destructive hover:bg-destructive-subtle active:bg-destructive-subtle/90",
    success: "border-transparent bg-transparent text-success hover:bg-success-subtle active:bg-success-subtle/90",
    warning: "border-transparent bg-transparent text-warning hover:bg-warning-subtle active:bg-warning-subtle/90",
    info: "border-transparent bg-transparent text-info hover:bg-info-subtle active:bg-info-subtle/90",
    neutral: "border-transparent bg-transparent text-neutral-subtle-foreground hover:bg-neutral-subtle active:bg-neutral-subtle/90",
  },
  link: {
    primary: "text-comp-link hover:text-comp-link-hover",
    secondary: "text-comp-text-secondary hover:text-comp-text-primary",
    danger: "text-destructive hover:text-destructive/90",
    success: "text-success hover:text-success/90",
    warning: "text-warning hover:text-warning/90",
    info: "text-info hover:text-info/90",
    neutral: "text-neutral-subtle-foreground hover:text-comp-text-primary",
  },
} as const;

export const badgeVariantStyles = {
  soft: "",
  solid: "",
  outline: "",
} as const;

export const badgeColorStyles = {
  soft: {
    primary: "bg-[var(--comp-accent-tint)] text-[var(--comp-accent)]",
    secondary: "bg-comp-elevated text-comp-text-secondary",
    danger: "bg-destructive-subtle text-destructive-subtle-foreground",
    success: "bg-success-subtle text-success-subtle-foreground",
    warning: "bg-warning-subtle text-warning-subtle-foreground",
    info: "bg-info-subtle text-info-subtle-foreground",
    neutral: "bg-neutral-subtle text-neutral-subtle-foreground",
    purple: "bg-purple-subtle text-purple-subtle-foreground",
    teal: "bg-teal-subtle text-teal-subtle-foreground",
    selection: "bg-selection-subtle text-selection-subtle-foreground",
  },
  solid: {
    primary: "bg-comp-accent text-comp-accent-foreground",
    secondary: "bg-comp-subtle text-comp-text-secondary",
    danger: "bg-destructive text-destructive-foreground",
    success: "bg-success text-success-foreground",
    warning: "bg-warning text-warning-foreground",
    info: "bg-info text-info-foreground",
    neutral: "bg-neutral-subtle text-neutral-subtle-foreground",
    purple: "bg-purple text-purple-foreground",
    teal: "bg-teal text-teal-foreground",
    selection: "bg-selection text-selection-foreground",
  },
  outline: {
    primary: "border border-[var(--comp-accent)]/30 bg-transparent text-[var(--comp-accent)]",
    secondary: "border border-comp-border-subtle bg-comp-surface text-comp-text-primary",
    danger: "border border-destructive-border bg-transparent text-destructive",
    success: "border border-success-border bg-transparent text-success",
    warning: "border border-warning-border bg-transparent text-warning",
    info: "border border-info-border bg-transparent text-info",
    neutral: "border border-neutral-border bg-transparent text-neutral-subtle-foreground",
    purple: "border border-purple-border bg-transparent text-purple-subtle-foreground",
    teal: "border border-teal-border bg-transparent text-teal-subtle-foreground",
    selection: "border border-selection-border bg-transparent text-selection-subtle-foreground",
  },
} as const;

export const colorVariants = {
  default: intentActionVariants.primary,
  destructive: intentActionVariants.destructive,
  success: intentActionVariants.success,
  warning: intentActionVariants.warning,
  info: intentActionVariants.info,
  neutral: intentActionVariants.neutral,
  outline: intentSurfaceVariants.outline,
  secondary: intentSurfaceVariants.secondary,
  ghost: intentSurfaceVariants.ghost,
} as const;

export function createColorCompoundVariants<Variant extends string, Color extends string>(
  styles: Record<Variant, Record<Color, string>>,
): { variant: Variant; color: Color; className: string }[] {
  return (Object.entries(styles) as [Variant, Record<Color, string>][]).flatMap(([variant, colors]) =>
    (Object.entries(colors) as [Color, string][]).map(([color, className]) => ({
      variant,
      color,
      className,
    }))
  );
}
